/**
 * System Health Diagnostics
 *
 * Displays webhook status, contract creation breakdown (normal vs recovery),
 * CLI setup instructions, and a test webhook tool.
 * Intended for admin/dev use only — not for sales reps.
 */

import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  ShieldAlert,
  ShieldCheck,
  Terminal,
  TestTube,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

export function SystemHealthDiagnostics() {
  const [lastWebhook, setLastWebhook] = useState<any>(null);
  const [webhookLoading, setWebhookLoading] = useState(true);
  const [contractStats, setContractStats] = useState({ normal: 0, recovery: 0 });
  const [testLoading, setTestLoading] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [testProposalId, setTestProposalId] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      // Fetch last webhook event
      const { data: webhookLogs } = await (supabase
        .from("activity_logs" as any)
        .select("*")
        .eq("event_type", "webhook_received")
        .order("created_at", { ascending: false })
        .limit(1) as any);
      setLastWebhook(webhookLogs?.[0] ?? null);
      setWebhookLoading(false);

      // Fetch contract breakdown
      const { data: contracts } = await supabase.from("contracts").select("id");
      if (contracts) {
        const normal = contracts.filter((c) => c.id.startsWith("contract_")).length;
        const recovery = contracts.filter((c) => c.id.startsWith("recovered_")).length;
        setContractStats({ normal, recovery });
      }
    };
    fetchData();
  }, []);

  const totalContracts = contractStats.normal + contractStats.recovery;
  const recoveryPercent = totalContracts > 0 ? ((contractStats.recovery / totalContracts) * 100).toFixed(1) : "0";

  const handleTestWebhook = async () => {
    if (!testProposalId.trim()) return;
    setTestLoading(true);
    setTestResult(null);

    try {
      const fakeEvent = {
        id: `evt_test_${Date.now()}`,
        type: "checkout.session.completed",
        data: {
          object: {
            id: `cs_test_${Date.now()}`,
            payment_status: "paid",
            metadata: {
              proposal_id: testProposalId.trim(),
              is_test: "true",
            },
            customer: null,
            customer_details: { email: "test@test.com" },
            payment_intent: `pi_test_${Date.now()}`,
          },
        },
      };

      const response = await supabase.functions.invoke("stripe-webhook", {
        body: fakeEvent,
        headers: { "Content-Type": "application/json" },
      });

      if (response.error) {
        setTestResult({ ok: false, message: response.error.message || "Function invocation failed" });
      } else {
        setTestResult({ ok: true, message: "Test webhook sent successfully. Check activity logs for results." });
      }
    } catch (err: any) {
      setTestResult({ ok: false, message: err.message || "Unexpected error" });
    }

    setTestLoading(false);
  };

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || "";

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Activity className="h-5 w-5 text-accent" />
          <CardTitle>System Health</CardTitle>
        </div>
        <CardDescription>Webhook status, contract integrity, and diagnostics.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Webhook Status */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold flex items-center gap-2">
            {lastWebhook ? (
              <ShieldCheck className="h-4 w-4 text-green-600" />
            ) : (
              <ShieldAlert className="h-4 w-4 text-yellow-600" />
            )}
            Stripe Webhook Status
          </h3>

          {webhookLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Checking...
            </div>
          ) : lastWebhook ? (
            <div className="rounded-lg border border-green-200 bg-green-50/50 p-3 text-sm space-y-1">
              <div className="flex items-center gap-2 text-green-700 font-medium">
                <CheckCircle2 className="h-4 w-4" />
                Webhook Active
              </div>
              <p className="text-muted-foreground">
                Last event: {new Date(lastWebhook.created_at).toLocaleString()}
              </p>
              {lastWebhook.metadata && (
                <p className="text-xs text-muted-foreground">
                  Event: {(lastWebhook.metadata as any)?.stripe_event_type} ({(lastWebhook.metadata as any)?.stripe_event_id})
                </p>
              )}
            </div>
          ) : (
            <div className="rounded-lg border border-yellow-200 bg-yellow-50/50 p-3 text-sm space-y-2">
              <div className="flex items-center gap-2 text-yellow-700 font-medium">
                <AlertTriangle className="h-4 w-4" />
                No Webhook Events Received
              </div>
              <p className="text-muted-foreground">
                Stripe webhooks have never fired. All contracts are being created via the recovery mechanism.
              </p>
            </div>
          )}
        </div>

        <Separator />

        {/* CLI Instructions */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold flex items-center gap-2">
            <Terminal className="h-4 w-4" />
            Webhook Setup (Test Mode)
          </h3>
          <div className="rounded-lg border bg-muted/30 p-3 space-y-2 text-sm">
            <p className="text-muted-foreground">To test webhooks locally, use the Stripe CLI:</p>
            <code className="block bg-background rounded p-2 text-xs font-mono break-all">
              stripe listen --forward-to {supabaseUrl}/functions/v1/stripe-webhook
            </code>
            <p className="text-muted-foreground text-xs">
              Copy the webhook signing secret (<code className="text-foreground">whsec_...</code>) and set it as <strong>STRIPE_WEBHOOK_SECRET</strong> in backend secrets.
            </p>
          </div>
        </div>

        <Separator />

        {/* Contract Breakdown */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold">Contract Creation Breakdown</h3>
          <div className="grid grid-cols-3 gap-4">
            <div className="rounded-lg border p-3 text-center">
              <p className="text-2xl font-bold text-foreground">{contractStats.normal}</p>
              <p className="text-xs text-muted-foreground">Normal (webhook)</p>
            </div>
            <div className="rounded-lg border p-3 text-center">
              <p className="text-2xl font-bold text-orange-600">{contractStats.recovery}</p>
              <p className="text-xs text-muted-foreground">Recovery</p>
            </div>
            <div className="rounded-lg border p-3 text-center">
              <p className="text-2xl font-bold text-foreground">{recoveryPercent}%</p>
              <p className="text-xs text-muted-foreground">Recovery Rate</p>
            </div>
          </div>
          {contractStats.recovery > 0 && contractStats.normal === 0 && (
            <div className="rounded-lg border border-orange-200 bg-orange-50/50 p-3 text-sm text-orange-700">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                All contracts were created via recovery. Configure the Stripe webhook to enable normal contract creation.
              </div>
            </div>
          )}
        </div>

        <Separator />

        {/* Test Webhook */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold flex items-center gap-2">
            <TestTube className="h-4 w-4" />
            Test Webhook (Admin Only)
          </h3>
          <p className="text-xs text-muted-foreground">
            Send a simulated <code>checkout.session.completed</code> event for an existing proposal. This bypasses Stripe signature verification.
          </p>
          <div className="flex gap-2">
            <Input
              placeholder="Proposal ID (e.g. proposal_...)"
              value={testProposalId}
              onChange={(e) => setTestProposalId(e.target.value)}
              className="flex-1"
            />
            <Button
              variant="outline"
              onClick={handleTestWebhook}
              disabled={testLoading || !testProposalId.trim()}
            >
              {testLoading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <TestTube className="h-4 w-4 mr-2" />
              )}
              Send Test
            </Button>
          </div>
          {testResult && (
            <div className={`rounded-lg border p-3 text-sm ${testResult.ok ? "border-green-200 bg-green-50/50 text-green-700" : "border-destructive/20 bg-destructive/5 text-destructive"}`}>
              <div className="flex items-center gap-2">
                {testResult.ok ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
                {testResult.message}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
